import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import type { components } from '@/api/schema';

export type EnarmPlus = components['schemas']['EnarmPlusOverview'];
export type Capitulo = components['schemas']['EnarmPlusChapter'];
export type BloquePlus = components['schemas']['EnarmPlusBlock'];
export type BloquePlusAlta = components['schemas']['EnarmPlusBlockCreateBody'];
export type BloquePlusCambio = components['schemas']['EnarmPlusBlockUpdateBody'];

const CLAVE = ['contenido', 'enarm-plus'];
const err = (e: unknown) => toast.error(mensajeDe(e));
const ruta = (id: number) => ({ params: { path: { id: String(id) } } });

export function useEnarmPlus() {
  return useQuery({
    queryKey: CLAVE,
    queryFn: () => unwrap<EnarmPlus>(api.GET('/api/v1/admin/enarm-plus')),
  });
}

export function useBloquesPlus(capitulo: number | null) {
  return useQuery({
    queryKey: [...CLAVE, 'bloques', capitulo],
    queryFn: () =>
      unwrap<BloquePlus[]>(
        api.GET('/api/v1/admin/enarm-plus/chapters/{id}/blocks', ruta(capitulo ?? 0)),
      ),
    enabled: capitulo !== null,
  });
}

export function useEnarmPlusMutaciones() {
  const qc = useQueryClient();
  const capitulos = (chapters: Capitulo[]) =>
    qc.setQueryData<EnarmPlus>(CLAVE, (d) => (d ? { ...d, chapters } : d));
  return {
    manual: useMutation({
      mutationFn: (body: components['schemas']['EnarmPlusManualBody']) =>
        unwrap<EnarmPlus>(api.PATCH('/api/v1/admin/enarm-plus', { body })),
      onSuccess: (d) => {
        qc.setQueryData(CLAVE, d);
        toast.success('Datos del manual guardados.');
      },
      onError: err,
    }),
    crearCapitulo: useMutation({
      mutationFn: (body: components['schemas']['EnarmPlusChapterCreateBody']) =>
        unwrap<Capitulo[]>(api.POST('/api/v1/admin/enarm-plus/chapters', { body })),
      onSuccess: capitulos,
      onError: err,
    }),
    editarCapitulo: useMutation({
      mutationFn: ({
        id,
        body,
      }: {
        id: number;
        body: components['schemas']['EnarmPlusChapterUpdateBody'];
      }) =>
        unwrap<Capitulo[]>(
          api.PATCH('/api/v1/admin/enarm-plus/chapters/{id}', { ...ruta(id), body }),
        ),
      onSuccess: capitulos,
      onError: err,
    }),
    borrarCapitulo: useMutation({
      mutationFn: (id: number) =>
        unwrap<Capitulo[]>(api.DELETE('/api/v1/admin/enarm-plus/chapters/{id}', ruta(id))),
      onSuccess: capitulos,
    }),
    ordenarCapitulos: useMutation({
      mutationFn: (ids: number[]) =>
        unwrap<Capitulo[]>(api.PUT('/api/v1/admin/enarm-plus/chapters/order', { body: { ids } })),
      onSuccess: capitulos,
      onError: err,
    }),
  };
}

export function useBloquesPlusMutaciones(capitulo: number) {
  const qc = useQueryClient();
  const poner = (b: BloquePlus[]) => {
    qc.setQueryData([...CLAVE, 'bloques', capitulo], b);
    void qc.invalidateQueries({ queryKey: CLAVE, exact: true });
  };
  const hijo = (childId: number) => ({
    params: { path: { id: String(capitulo), childId: String(childId) } },
  });
  return {
    agregar: useMutation({
      mutationFn: (body: BloquePlusAlta) =>
        unwrap<BloquePlus[]>(
          api.POST('/api/v1/admin/enarm-plus/chapters/{id}/blocks', { ...ruta(capitulo), body }),
        ),
      onSuccess: poner,
      onError: err,
    }),
    editar: useMutation({
      mutationFn: ({ childId, body }: { childId: number; body: BloquePlusCambio }) =>
        unwrap<BloquePlus[]>(
          api.PATCH('/api/v1/admin/enarm-plus/chapters/{id}/blocks/{childId}', {
            ...hijo(childId),
            body,
          }),
        ),
      onSuccess: poner,
      onError: err,
    }),
    quitar: useMutation({
      mutationFn: (childId: number) =>
        unwrap<BloquePlus[]>(
          api.DELETE('/api/v1/admin/enarm-plus/chapters/{id}/blocks/{childId}', hijo(childId)),
        ),
      onSuccess: poner,
      onError: err,
    }),
    ordenar: useMutation({
      mutationFn: (ids: number[]) =>
        unwrap<BloquePlus[]>(
          api.PUT('/api/v1/admin/enarm-plus/chapters/{id}/blocks/order', {
            ...ruta(capitulo),
            body: { ids },
          }),
        ),
      onSuccess: poner,
      onError: err,
    }),
  };
}
